import createClient from 'openapi-fetch';
import type { paths } from './schema';
import { tokens } from './tokens';

/** Endpoints that must never trigger a refresh or carry credentials of a dead session. */
const AUTH_PATHS = ['/auth/login', '/auth/register', '/auth/refresh', '/auth/google', '/auth/forgot-password', '/auth/reset-password'];

/** Answered without a session, so they never wait for a refresh. */
const PUBLIC_PATHS = ['/config', '/version', '/health', '/billing/plans'];

type SignedOutHandler = () => void;
let onSignedOut: SignedOutHandler = () => {};
export function setSignedOutHandler(fn: SignedOutHandler): void {
  onSignedOut = fn;
}

/** `unavailable` = the server could not be asked (offline, 5xx, throttled): the session may still be good. */
export type RefreshOutcome = 'ok' | 'dead' | 'unavailable';

let refreshing: Promise<RefreshOutcome> | null = null;

/** One refresh at a time: parallel 401s share the same request, because the server rotates refresh tokens. */
export function refreshSession(): Promise<RefreshOutcome> {
  refreshing ??= doRefresh().finally(() => {
    refreshing = null;
  });
  return refreshing;
}

async function doRefresh(): Promise<RefreshOutcome> {
  const refresh = tokens.refresh();
  if (!refresh) return 'dead';
  try {
    const res = await fetch(`${location.origin}/api/v1/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refresh }),
    });
    if (res.status >= 500 || res.status === 429) return 'unavailable';
    if (!res.ok) {
      if (res.status === 401) tokens.clear(); // the token is really dead
      return 'dead';
    }
    const pair = (await res.json()) as { access_token: string; refresh_token: string };
    tokens.setPair(pair.access_token, pair.refresh_token);
    return 'ok';
  } catch {
    return 'unavailable'; // network failure: do not sign the user out because of a bad connection
  }
}

function withAuth(req: Request): Request {
  const headers = new Headers(req.headers);
  const access = tokens.access();
  if (access) headers.set('Authorization', `Bearer ${access}`);
  const ws = tokens.workspaceId();
  if (ws) headers.set('X-Workspace-Id', ws);
  return new Request(req, { headers });
}

function isAuthPath(url: string): boolean {
  const path = new URL(url, location.origin).pathname.replace(/^\/api\/v1/, '');
  return AUTH_PATHS.includes(path);
}

/**
 * The access token lives in memory, so after a reload there is none while the refresh token is still saved. Sending the
 * first requests anyway would cost a 401 and a retry for each; refreshing first saves that round trip on every start.
 * If the refresh fails the request goes out as before and the usual 401 handling decides what that means.
 */
async function refreshBeforeFirstCall(req: Request): Promise<void> {
  if (tokens.access() || !tokens.refresh() || isAuthPath(req.url) || isPublicPath(req.url)) return;
  await refreshSession();
}

function isPublicPath(url: string): boolean {
  return PUBLIC_PATHS.includes(new URL(url, location.origin).pathname.replace(/^\/api\/v1/, ''));
}

/** fetch used by the typed client: adds credentials and transparently refreshes once on 401. */
export async function authedFetch(req: Request): Promise<Response> {
  await refreshBeforeFirstCall(req);
  const retry = req.clone();
  const res = await fetch(withAuth(req));
  if (res.status !== 401 || isAuthPath(req.url) || !tokens.refresh()) return res;
  const outcome = await refreshSession();
  if (outcome === 'unavailable') return res; // callers see a 401; queued writes treat it as "try again later"
  if (outcome === 'dead') {
    onSignedOut();
    return res;
  }
  const second = await fetch(withAuth(retry));
  if (second.status === 401) onSignedOut();
  return second;
}

// Absolute same-origin URL: browsers resolve relative ones, but Request in Node/jsdom does not.
export const api = createClient<paths>({ baseUrl: `${location.origin}/api/v1`, fetch: authedFetch });
